/**
 * GPX Loader Module
 * Handles loading and parsing GPX (GPS Exchange Format) files
 */

/**
 * Parse GPX XML to GeoJSON
 * @param {string} gpxText - GPX XML content
 * @returns {Object} GeoJSON FeatureCollection
 */
function parseGPX(gpxText) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(gpxText, 'application/xml');

    // Check for parse errors
    const parseError = doc.querySelector('parsererror');
    if (parseError) {
        throw new Error('Invalid GPX file: XML parsing error');
    }

    const features = [];
    let featureIndex = 0;

    // Parse waypoints (wpt)
    const waypoints = doc.querySelectorAll('wpt');
    waypoints.forEach(wpt => {
        const lat = parseFloat(wpt.getAttribute('lat'));
        const lon = parseFloat(wpt.getAttribute('lon'));

        if (isNaN(lat) || isNaN(lon)) return;

        const properties = {
            __featureIndex: featureIndex++,
            type: 'waypoint'
        };

        // Extract common GPX elements
        const name = wpt.querySelector('name')?.textContent;
        const desc = wpt.querySelector('desc')?.textContent;
        const ele = wpt.querySelector('ele')?.textContent;
        const time = wpt.querySelector('time')?.textContent;
        const cmt = wpt.querySelector('cmt')?.textContent;
        const sym = wpt.querySelector('sym')?.textContent;

        if (name) properties.name = name;
        if (desc) properties.description = desc;
        if (ele) properties.elevation = parseFloat(ele);
        if (time) properties.time = time;
        if (cmt) properties.comment = cmt;
        if (sym) properties.symbol = sym;

        features.push({
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [lon, lat, ele ? parseFloat(ele) : undefined].filter(v => v !== undefined)
            },
            properties
        });
    });

    // Parse tracks (trk)
    const tracks = doc.querySelectorAll('trk');
    tracks.forEach(trk => {
        const name = trk.querySelector('name')?.textContent;
        const desc = trk.querySelector('desc')?.textContent;
        const cmt = trk.querySelector('cmt')?.textContent;

        const trackSegments = trk.querySelectorAll('trkseg');
        trackSegments.forEach(seg => {
            const trackPoints = seg.querySelectorAll('trkpt');
            const coordinates = [];

            trackPoints.forEach(pt => {
                const lat = parseFloat(pt.getAttribute('lat'));
                const lon = parseFloat(pt.getAttribute('lon'));
                const ele = pt.querySelector('ele')?.textContent;

                if (!isNaN(lat) && !isNaN(lon)) {
                    const coord = [lon, lat];
                    if (ele) coord.push(parseFloat(ele));
                    coordinates.push(coord);
                }
            });

            if (coordinates.length > 0) {
                const properties = {
                    __featureIndex: featureIndex++,
                    type: 'track'
                };

                if (name) properties.name = name;
                if (desc) properties.description = desc;
                if (cmt) properties.comment = cmt;

                features.push({
                    type: 'Feature',
                    geometry: {
                        type: 'LineString',
                        coordinates
                    },
                    properties
                });
            }
        });
    });

    // Parse routes (rte)
    const routes = doc.querySelectorAll('rte');
    routes.forEach(rte => {
        const name = rte.querySelector('name')?.textContent;
        const desc = rte.querySelector('desc')?.textContent;
        const cmt = rte.querySelector('cmt')?.textContent;

        const routePoints = rte.querySelectorAll('rtept');
        const coordinates = [];

        routePoints.forEach(pt => {
            const lat = parseFloat(pt.getAttribute('lat'));
            const lon = parseFloat(pt.getAttribute('lon'));
            const ele = pt.querySelector('ele')?.textContent;

            if (!isNaN(lat) && !isNaN(lon)) {
                const coord = [lon, lat];
                if (ele) coord.push(parseFloat(ele));
                coordinates.push(coord);
            }
        });

        if (coordinates.length > 0) {
            const properties = {
                __featureIndex: featureIndex++,
                type: 'route'
            };

            if (name) properties.name = name;
            if (desc) properties.description = desc;
            if (cmt) properties.comment = cmt;

            features.push({
                type: 'Feature',
                geometry: {
                    type: 'LineString',
                    coordinates
                },
                properties
            });
        }
    });

    return {
        type: 'FeatureCollection',
        features
    };
}

/**
 * Load and parse GPX file
 * @param {File} file - GPX file
 * @param {Function} progressCallback - Progress callback (progress, message)
 * @returns {Object} Result with geojson, fileName, and featureCount
 */
export async function loadGPX(file, progressCallback) {
    try {
        progressCallback(10, 'Reading GPX file...');

        // Read file as text
        const text = await file.text();

        progressCallback(50, 'Parsing GPX data...');

        // Parse GPX
        const geojson = parseGPX(text);

        if (geojson.features.length === 0) {
            throw new Error('No valid features found in GPX file');
        }

        progressCallback(90, 'Processing complete...');

        // Detect geometry types
        const geometryTypes = [...new Set(geojson.features.map(f => f.geometry.type))];

        return {
            geojson,
            fileName: file.name.replace(/\.gpx$/i, ''),
            featureCount: geojson.features.length,
            geometryTypes
        };

    } catch (error) {
        console.error('Error loading GPX:', error);
        throw error;
    }
}
